import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import SendBird, { GroupChannel } from "sendbird";
import {
  sbCreatePersonalChatChannelListQuery,
  sbGetPersonalChannelsByName,
  sbGetTotalUnreadChannelCount,
} from "../utils/sb";

type PersonalChatState = {
  query: SendBird.GroupChannelListQuery | null;
  isLoading: boolean;
  isRefreshing: boolean;
  isFetching: boolean;
  error: string | null;
  channels: SendBird.GroupChannel[];
  unreadChannelCount: number;
  forceRefresh: boolean;
};

const mainInboxSlice = createSlice({
  name: "mainInbox",
  initialState: {
    query: null,
    isLoading: true,
    isRefreshing: false,
    isFetching: false,
    error: null,
    channels: [],
    unreadChannelCount: 0,
    forceRefresh: false,
  } as PersonalChatState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload;
    },
    setFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setQuery: (state, action) => {
      state.query = action.payload;
    },
    setUnreadChannelCount: (state, action) => {
      state.unreadChannelCount = action.payload;
    },
    setForceRefresh: (state, action) => {
      state.forceRefresh = action.payload;
    },
    setChannels: (
      state,
      {
        payload: { channels, reset = false },
      }: PayloadAction<{
        channels: SendBird.GroupChannel[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.channels = channels;
      } else {
        const mergedChannels = [...state.channels, ...channels];
        state.channels = mergedChannels;
      }
    },
    refresh: (state) => {
      state.isRefreshing = true;
      state.error = null;
    },
    resetMainInboxState: (state) => {
      state.query = null;
      state.channels = [];
      state.isLoading = false;
      state.isFetching = false;
      state.isRefreshing = false;
      state.error = null;
    },
    setUnreadMessageCount: (
      state,
      {
        payload: { url, unreadMessageCount },
      }: PayloadAction<{ url: string; unreadMessageCount: number }>
    ) => {
      const updatedChannels = state.channels.map((channel) => {
        if (channel.url === url) {
          return { ...channel, unreadMessageCount };
        }

        return channel;
      });
      state.channels = updatedChannels;
    },
    replaceChannel: (
      state,
      { payload: { channel } }: PayloadAction<{ channel: GroupChannel }>
    ) => {
      const updatedChannels = state.channels.map((ch) => {
        if (ch.url === channel.url) {
          return channel;
        }

        return ch;
      });

      const sortedUpdatedChannels = updatedChannels.sort(
        (a, b) => b?.lastMessage?.createdAt - a?.lastMessage?.createdAt
      );

      state.channels = sortedUpdatedChannels;
    },
  },
});

export const {
  setLoading,
  setRefreshing,
  setFetching,
  setError,
  setChannels,
  setQuery,
  refresh,
  setUnreadMessageCount,
  resetMainInboxState,
  setUnreadChannelCount,
  replaceChannel,
  setForceRefresh,
} = mainInboxSlice.actions;

export const fetchMainInboxChannels = () => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    dispatch(setChannels({ channels: [], reset: true }));
    dispatch(setQuery(sbCreatePersonalChatChannelListQuery()));
  } catch (error) {
    dispatch(setError(error?.message));
  }
};

export const fetchNextMainInboxChannels = () => async (dispatch, getState) => {
  try {
    const {
      mainInbox: { query },
    } = getState();
    dispatch(setFetching(true));
    const fetchedChannels = await query?.next();
    dispatch(setChannels({ channels: fetchedChannels }));
  } catch (error) {
    console.log("error", error);
  } finally {
    dispatch(setFetching(false));
    dispatch(setLoading(false));
    dispatch(setForceRefresh(false));
  }
};

export const fetchMainInboxChannelsByName =
  (searchText) => async (dispatch) => {
    try {
      dispatch(setQuery(null));
      // dispatch(setSearching(true));
      const channels = await sbGetPersonalChannelsByName(searchText);
      dispatch(setChannels({ channels: channels, reset: true }));
    } catch (error) {
      console.log("error", error);
    } finally {
      // dispatch(setSearching(false));
    }
  };

export const refreshMainInboxChannels = () => async (dispatch) => {
  dispatch(setRefreshing(true));
  dispatch(setError(null));
  try {
    await dispatch(fetchMainInboxChannels());
  } catch (error) {
    dispatch(setError(error?.message));
  } finally {
    dispatch(setRefreshing(false));
  }
};

export const fetchChannelUnreadCount = () => async (dispatch) => {
  try {
    const count = await sbGetTotalUnreadChannelCount();
    dispatch(setUnreadChannelCount(count));
  } catch (error) {
    console.log("error", error);
  }
};

export default mainInboxSlice.reducer;
